import { Drawer, Progress } from "antd";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PortalHeader } from "../../components/molecules/Header";
import { ButtonT1 } from "../../components/atoms/Button";
import {
  useConfirmOnboardingStepMutation,
  useGetOnboardingQuery,
  useSkipOnboardingStepMutation,
  useUnskipOnboardingStepMutation,
  useUpdateOnboardingPreferencesMutation,
} from "../../store/apiSlice/onboardingApiSlice";
import { StepRow } from "./components/StepRow";
import { useSetupGuide } from "./SetupGuideContext";
import { DrawerBody, SectionBlock, SectionTitle } from "./SetupGuide.styles";
import styled from "styled-components";

const FinishPanel = styled.div`
  background: #eaf7f0;
  border: 1px solid #b7e4c9;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;

  h3 {
    color: #14532d;
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 6px;
  }

  p {
    color: #1f3b2c;
    font-size: 13px;
    margin: 0 0 12px;
  }

  .actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
`;

export function SetupGuideDrawer() {
  const navigate = useNavigate();
  const { drawerOpen, focusSection, closeSetupGuide } = useSetupGuide();
  const { data, isFetching } = useGetOnboardingQuery(undefined, {
    skip: !drawerOpen,
    refetchOnMountOrArgChange: true,
    pollingInterval: drawerOpen ? 15000 : 0,
  });
  const [skipStep, { isLoading: skipping }] = useSkipOnboardingStepMutation();
  const [unskipStep] = useUnskipOnboardingStepMutation();
  const [confirmStep, { isLoading: confirming }] = useConfirmOnboardingStepMutation();
  const [updatePreferences, { isLoading: hiding }] = useUpdateOnboardingPreferencesMutation();

  const progress = data?.progress;
  const sections = data?.sections || [];

  const subtitle = useMemo(() => {
    if (!progress?.total) {
      return "Setup guide";
    }
    return `${progress.completed} of ${progress.total} complete`;
  }, [progress]);

  const hasSections = sections.length > 0;
  useEffect(() => {
    if (!drawerOpen || !focusSection || !hasSections) {
      return;
    }
    document
      .querySelector(`[data-setup-section="${focusSection}"]`)
      ?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [drawerOpen, focusSection, hasSections]);

  const onNavigate = path => {
    closeSetupGuide();
    navigate(path);
  };

  const onHide = async () => {
    await updatePreferences({ hidden: true }).unwrap();
    closeSetupGuide();
  };

  const showFinish = progress?.percent === 100 && data?.preferences?.completed_seen === false;

  const markFinishSeen = () => {
    if (showFinish) updatePreferences({ completed_seen: true });
  };

  const onFinishCreateOrder = () => {
    markFinishSeen();
    onNavigate("/sales/order/create");
  };

  const onFinishHide = async () => {
    await updatePreferences({ hidden: true, completed_seen: true }).unwrap();
    closeSetupGuide();
  };

  const onClose = () => {
    markFinishSeen();
    closeSetupGuide();
  };

  return (
    <Drawer
      open={drawerOpen}
      onClose={onClose}
      width={420}
      closable={false}
      title={<PortalHeader title={`Setup guide · ${subtitle}`} onClose={onClose} />}
      styles={{ header: { padding: 0 }, body: { padding: 0 } }}
      destroyOnClose={false}
    >
      <DrawerBody>
        {showFinish ? (
          <FinishPanel role="status">
            <h3>You're all set.</h3>
            <p>
              Setup's done — the boring part is officially behind you. Your first order can go out
              today.
            </p>
            <div className="actions">
              <ButtonT1
                title="Create sales order"
                type="button"
                onClickCallback={onFinishCreateOrder}
              />
              <ButtonT1
                title="Hide setup guide"
                $variant="primaryLight"
                type="button"
                loading={hiding}
                onClickCallback={onFinishHide}
              />
            </div>
          </FinishPanel>
        ) : null}
        {progress?.total ? (
          <Progress
            percent={progress.percent}
            size="small"
            status={progress.percent === 100 ? "success" : "active"}
            style={{ marginBottom: 16 }}
          />
        ) : null}
        {isFetching && !data ? <p>Loading setup steps…</p> : null}
        {sections.map(section => (
          <SectionBlock key={section.key} data-setup-section={section.key}>
            <SectionTitle>{section.title}</SectionTitle>
            {section.steps.map(step => (
              <StepRow
                key={step.key}
                step={step}
                onNavigate={onNavigate}
                onSkip={key => skipStep(key)}
                onUnskip={key => unskipStep(key)}
                onConfirm={key => confirmStep(key)}
                skipLoading={skipping}
                confirmLoading={confirming}
              />
            ))}
          </SectionBlock>
        ))}
        <div style={{ marginTop: 24 }}>
          <ButtonT1
            title="Hide reminders for me"
            $variant="primaryLight"
            type="button"
            loading={hiding}
            onClickCallback={onHide}
          />
          <p style={{ fontSize: 12, color: "#5e6278", marginTop: 8 }}>
            Reopen the guide from Setup guide next to View Instructions on integration screens,
            or Organization → Setup guide.
          </p>
        </div>
      </DrawerBody>
    </Drawer>
  );
}
